/**
 * Inline cards under a TruInsight answer. The whole card is the link when the server
 * supplied a catalog path.
 */
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { getApiErrorMessage } from '@/api/client';
import { openZendeskHelpCenterSso } from '@/features/support/lib/zendesk-sso';
import { isReleaseFeatureEnabled } from '@/lib/release-flags';
import { useAuthStore } from '@/stores/auth.store';
import { toast } from '@/stores/toast.store';
import { isSafeInsightHref } from '../lib/ai-cards';
import type { AiCard } from '../types';

type InsightCardsProps = {
  cards: AiCard[];
};

const SUPPORT_HREF = '/support';
const CARD_LINK_CLASS =
  'block w-full rounded-lg border border-border bg-card px-3 py-2.5 text-left text-card-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

export function InsightCards({ cards }: InsightCardsProps) {
  if (cards.length === 0) return null;
  return (
    <ul className="space-y-2">
      {cards.map((card, index) => (
        <li key={`${card.type}-${index}`}>
          <InsightCard card={card} />
        </li>
      ))}
    </ul>
  );
}

function InsightCard({ card }: { card: AiCard }) {
  const href = card.href && isSafeInsightHref(card.href) ? card.href : undefined;
  const body = <CardBody card={card} linked={href != null} />;
  if (!href) {
    return (
      <div className="rounded-lg border border-border bg-card px-3 py-2.5 text-card-foreground">
        {body}
      </div>
    );
  }
  // With Zendesk on, `/support` redirects to Properties; Support is the Help Center, as in the sidebar.
  if (href === SUPPORT_HREF && isReleaseFeatureEnabled('support-zendesk')) {
    return (
      <button type="button" onClick={handleOpenZendeskSupport} className={CARD_LINK_CLASS}>
        {body}
      </button>
    );
  }
  return (
    <Link to={href} className={CARD_LINK_CLASS}>
      {body}
    </Link>
  );
}

async function handleOpenZendeskSupport(): Promise<void> {
  const userId = useAuthStore.getState().user?.userId;
  if (typeof userId !== 'number') {
    toast.error('Could not open Support. Please sign in again.');
    return;
  }
  try {
    await openZendeskHelpCenterSso(userId);
  } catch (err) {
    toast.error(getApiErrorMessage(err, 'Could not open Support. Please try again.'));
  }
}

function CardBody({ card, linked }: { card: AiCard; linked: boolean }) {
  if (card.type === 'metric') {
    const label = card.facts[0]?.label ?? card.title;
    return (
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-lg font-semibold tabular-nums text-foreground">{card.title}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-semibold text-foreground">{card.title}</p>
        {linked ? <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden /> : null}
      </div>
      {card.facts.length > 0 ? (
        <dl className="mt-1 space-y-0.5">
          {card.facts.map((fact) => (
            <div key={fact.label} className="flex gap-2 text-xs">
              <dt className="text-muted-foreground">{fact.label}</dt>
              <dd className="font-medium tabular-nums text-foreground">{fact.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {linked && card.actionLabel ? (
        <p className="mt-1.5 text-xs font-medium text-primary-text">{card.actionLabel}</p>
      ) : null}
    </div>
  );
}
